import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";

const EMPTY_FORM = {
  title: "",
  excerpt: "",
  body: "",
  tags: "",
  status: "draft",
  seo_description: "",
  cover_image_url: "",
};

function formToPayload(form, overrideStatus = null) {
  return {
    ...form,
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    status: overrideStatus || form.status,
  };
}

function BlogFormFields({ form, setForm }) {
  return (
    <>
      <div>
        <label className="font-manrope text-sm text-ink mb-1 block">Title</label>
        <input
          required maxLength={200}
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="input-soft"
          placeholder="5 unexpected must-haves for hysterectomy recovery"
          data-testid="blog-editor-title"
        />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <label className="font-manrope text-sm text-ink mb-1 block">Excerpt (shown on listing)</label>
          <textarea
            maxLength={500} rows={3}
            value={form.excerpt}
            onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
            className="input-soft"
            data-testid="blog-editor-excerpt"
          />
        </div>
        <div>
          <label className="font-manrope text-sm text-ink mb-1 block">SEO description (&lt;= 300 chars)</label>
          <textarea
            maxLength={300} rows={3}
            value={form.seo_description}
            onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
            className="input-soft"
            data-testid="blog-editor-seo"
          />
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div>
          <label className="font-manrope text-sm text-ink mb-1 block">Cover image URL</label>
          <input
            value={form.cover_image_url}
            onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
            className="input-soft"
            placeholder="https://…"
            data-testid="blog-editor-cover"
          />
        </div>
        <div>
          <label className="font-manrope text-sm text-ink mb-1 block">Tags (comma-separated)</label>
          <input
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            className="input-soft"
            placeholder="recovery, self-care"
            data-testid="blog-editor-tags"
          />
        </div>
      </div>
      <div>
        <label className="font-manrope text-sm text-ink mb-1 block">Status</label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger className="max-w-xs" data-testid="blog-editor-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="font-manrope text-sm text-ink mb-1 block">Body (Markdown supported)</label>
        <textarea
          required rows={22}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          className="input-soft font-mono text-sm"
          placeholder={`# Heading\n\nSome **bold** text with a [link](https://example.com).\n\n- bullet\n- bullet\n\n## Subheading\n\nMore content…`}
          data-testid="blog-editor-body"
        />
        <p className="font-manrope text-xs text-ink-soft mt-1">
          Markdown: **bold**, *italic*, # headings, - lists, [links](url), `code`.
        </p>
      </div>
    </>
  );
}

function BlogFormActions({ onCancel, onSaveDraft, onPublish, submitting }) {
  return (
    <div className="flex gap-3 justify-end">
      <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
      <button
        type="submit"
        disabled={submitting}
        className="btn-ghost"
        data-testid="blog-editor-save-draft"
        onClick={onSaveDraft}
      >
        {submitting ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        disabled={submitting}
        className="btn-primary"
        onClick={onPublish}
        data-testid="blog-editor-publish"
      >
        {submitting ? "Publishing…" : "Save & publish"}
      </button>
    </div>
  );
}

export default function BlogEditor({ post, onBack }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (post) {
      setForm({
        title: post.title || "",
        excerpt: post.excerpt || "",
        body: post.body || "",
        tags: (post.tags || []).join(", "),
        status: post.status || "draft",
        seo_description: post.seo_description || "",
        cover_image_url: post.cover_image_url || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [post]);

  const save = async (payload) => {
    setSubmitting(true);
    try {
      if (post) {
        await api.put(`/admin/blog/posts/${post.id}`, payload);
        toast.success("Saved.");
      } else {
        await api.post("/admin/blog/posts", payload);
        toast.success("Created.");
      }
      onBack();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    save(formToPayload(form));
  };

  const handlePublishClick = () => {
    save(formToPayload(form, "published"));
  };

  return (
    <div data-testid="blog-editor">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand mb-6"
        data-testid="blog-editor-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to posts
      </button>
      <form onSubmit={handleFormSubmit} className="space-y-5 max-w-4xl">
        <BlogFormFields form={form} setForm={setForm} />
        <BlogFormActions
          onCancel={onBack}
          onSaveDraft={() => { /* default form submit handles save with current status */ }}
          onPublish={handlePublishClick}
          submitting={submitting}
        />
      </form>
    </div>
  );
}
